import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";
import moment from "moment";
import {
  ArrowLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Clock,
  Users,
  Building2,
  FileText,
  MapPin,
  UserCog,
  Send,
  Camera,
} from "lucide-react";

const SectionAGeneralInformation = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const { loading, error: apiError } = useSelector((state) => state.canteen);

  const canteen = state?.canteen || {};
  console.log("canteen>>",canteen);
  
  const surveyDetail = state?.surveyDetail || {};
  const surveyId = state?.surveyId;
  const newSurvey = state?.newSurvey;

  const districtId = canteen.district_id || state?.district_id;
  const municipalityId = canteen.municipality_id || state?.municipality_id;
  const wardId = canteen.ward_id || state?.ward_id;
  const areaId = canteen.id || state?.area_id || state?.canteenId;
  const canteenTypeId =
    canteen.canteen_type_id || canteen.canteen_type || state?.canteen_type_id;

  const [form, setForm] = useState({
    a1: "",
    a1Remarks: "",
    a1Document: null,
    a2: "",
    a2Remarks: "",
    a3: "",
    a4From: "12:00",
    a4To: "15:00",
    a5: "",
    a6: "",
    fullAddress:
      canteen?.address || canteen?.kitchen_location_fulladdress || "",
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [coords, setCoords] = useState({ latitude: null, longitude: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);
  const [previewImages, setPreviewImages] = useState({});
  const [previewModal, setPreviewModal] = useState(null);

  useEffect(() => {
    try {
      const sectionData = localStorage.getItem("survey_section_a_kiosk");
      if (sectionData)
        setForm((prev) => ({ ...prev, ...JSON.parse(sectionData) }));
    } catch (err) {
      console.error("Error loading saved data:", err);
    }
  }, []);

  useEffect(() => {
    if (surveyDetail?.gen_data) {
      const genData = surveyDetail.gen_data;
      setForm((prev) => ({
        ...prev,
        fullAddress: genData.kiosk_location_fulladdress || prev.fullAddress,
        a1:
          genData.gen_info_A1 === 1
            ? t.global.yes
            : genData.gen_info_A1 === 2
              ? t.global.no
              : "",
        a1Remarks: genData.gen_info_A1_remarks || "",
        a2:
          genData.gen_info_A2 === 1
            ? t.global.shgs
            : genData.gen_info_A2 === 2
              ? t.global.others
              : "",
        a2Remarks: genData.gen_info_A2_remarks || "",
        a3: genData.gen_info_A3 || "",
        a4From: genData.gen_info_A4?.slice(0,5)|| "12:00",
        a4To: genData.gen_info_A4_to?.slice(0,5)|| "15:00",
        a5: genData.gen_info_A5 || "",
        a6:
          genData.gen_info_A6 === 1
            ? t.global.yes
            : genData.gen_info_A6 === 2
              ? t.global.no
              : "",
      }));
    }
  }, [surveyDetail, t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        Object.values(form).some(
          (v) => v !== "" && v !== null && v !== undefined,
        )
      ) {
        localStorage.setItem("survey_section_a_kiosk", JSON.stringify(form));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, [name]: t.global.err_file_size }));
        return;
      }
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({ ...prev, [name]: t.global.err_file_type }));
        return;
      }
      setForm((prev) => ({ ...prev, [name]: file }));
      if (file.type.startsWith("image/")) {
        setPreviewImages((prev) => ({
          ...prev,
          [name]: URL.createObjectURL(file),
        }));
      }
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) =>
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            }),
          (error) => {
            console.warn("Geolocation error:", error);
            reject(error);
          },
          { enableHighAccuracy: true, timeout: 10000 },
        );
      } else {
        reject(new Error("Geolocation not supported"));
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.fullAddress?.trim()) newErrors.fullAddress = t.global.err_address;
    if (!form.a1) newErrors.a1 = t.global.err_a1;
    if (!form.a2) newErrors.a2 = t.global.err_a2;
    if (!form.a3 || form.a3 < 0) newErrors.a3 = t.global.err_a3;
    if (!form.a4From || !form.a4To) newErrors.a4 = t.global.err_a4;
    else if (form.a4From >= form.a4To) newErrors.a4 = t.global.err_a4_order;
    if (!form.a5 || form.a5 < 0) newErrors.a5 = t.global.err_a5;
    if (!form.a6) newErrors.a6 = t.global.err_a6;
    if (form.a1 === t.global.no && !form.a1Remarks?.trim()) {
      newErrors.a1Remarks = t.global.err_a1;
    }
    if (form.a2 === t.global.others && !form.a2Remarks?.trim()) {
      newErrors.a2Remarks = t.global.err_a2;
    }
    if (form.a1Document && form.a1Document.size > 5 * 1024 * 1024)
      newErrors.a1Document = t.global.err_file_size;
    if (form.a1Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.a1Document.type))
        newErrors.a1Document = t.global.err_file_type_long;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (
      !districtId ||
      !municipalityId ||
      !wardId ||
      !areaId ||
      !canteenTypeId
    ) {
      alert(t.global.alert_missing_ids);
      return;
    }
    if (!validateForm()) {
      alert(t.global.alert_fix_errors);
      return;
    }

    setIsSubmitting(true);
    let locationString = "";
    try {
      const location = await getCurrentLocation();
      locationString = `${location.latitude},${location.longitude}`;
      setCoords(location);
    } catch (err) {
      console.warn("Location not available, continuing without it");
    }

    const fd = new FormData();
    fd.append("district_id", districtId);
    fd.append("municipality_id", municipalityId);
    fd.append("ward_id", wardId);
    fd.append("area_id", areaId);
    fd.append("canteen_type_id", canteenTypeId);
    if (canteen?.parent_kitchen?.id)
      fd.append("parent_kitchen_id", canteen.parent_kitchen.id);
    fd.append("lattitude_longitude", locationString);
    fd.append("reporting_date", moment().format("YYYY-MM-DD"));
    fd.append("current_section", "geninfo");
    fd.append("kiosk_location_fulladdress", form.fullAddress || "");
    fd.append("gen_info_A1", form.a1 === t.global.yes ? 1 : 2);
    fd.append("gen_info_A1_remarks", form.a1Remarks || "");
    fd.append("gen_info_A2", form.a2 === t.global.shgs ? 1 : 2);
    fd.append("gen_info_A2_remarks", form.a2Remarks || "");
    fd.append("gen_info_A3", form.a3 || "");
    fd.append("gen_info_A4", form.a4From || "");
    fd.append("gen_info_A4_to", form.a4To || "");
    fd.append("gen_info_A5", form.a5 || "");
    fd.append("gen_info_A6", form.a6 === t.global.yes ? 1 : 2);
    if (form.a1Document) fd.append("gen_info_A1_upload", form.a1Document);
    if (surveyId) fd.append("survey_id", surveyId);
    try {
      const response = await dispatch(createCanteen(fd));
      if (response?.data || response?.payload) {
        const completedSections = JSON.parse(
          localStorage.getItem("completedSections") || "{}",
        );
        completedSections.a = "complete";
        localStorage.setItem(
          "completedSections",
          JSON.stringify(completedSections),
        );
        localStorage.removeItem("survey_section_a_kiosk");
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          navigate("/survey/sections", {
            state: {
              canteen,
              surveyDetail: {
                ...surveyDetail,
                gen_data: {
                  ...surveyDetail.gen_data,
                  is_gen_info_survey_done: 1,
                },
              },
              surveyId,
              newSurvey,
            },
          });
        }, 2000);
      } else {
        throw new Error("API did not confirm success");
      }
    } catch (err) {
      console.error("Submission failed, saving locally:", err);
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.a = "partial";
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );
      localStorage.setItem(
        "survey_section_a_kiosk",
        JSON.stringify({
          ...form,
          savedLocally: true,
          submittedAt: new Date().toISOString(),
        }),
      );
      alert(t.global.alert_saved_locally);
      navigate("/survey/sections", { state: { canteen, surveyId, newSurvey } });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSurveyTypeDisplay = () => {
    const type = canteen.surveyType || state?.surveyType;
    switch (type) {
      case "kiosk":
        return t.global.survey_type_kiosk;
      case "kitchen":
        return t.global.survey_type_kitchen;
      case "kitchendining":
      case "kitchencumDining":
        return t.global.survey_type_kitchendining;
      default:
        return t.global.survey_type_default;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">{t.global.success_message}</p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", {
                    state: { canteen, surveyId, newSurvey },
                  });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kiosk.sections.A.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kiosk.sections.A.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full flex items-center">
                <Building2 size={14} className="mr-1" />
                {getSurveyTypeDisplay()} {t.global.survey}
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {canteen.area_name
                  ? `${t.global.canteen_name_label} ${canteen.area_name}`
                  : t.global.na}
              </div>
              {canteen.id && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {canteen.id}
                </div>
              )}
            </div>
          </div>

          {/* Location Debug Info */}
          {/* <div className="mb-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
            <p className="font-medium">{t.global.location_info}</p>
            <p>
              {t.global.district_id}: {districtId || t.global.na} |{" "}
              {t.global.municipality_id}: {municipalityId || t.global.na} |{" "}
              {t.global.ward_id}: {wardId || t.global.na}
            </p>
            <p>
              {t.global.area_id}: {areaId || t.global.na} |{" "}
              {t.global.canteen_type_id}: {canteenTypeId || t.global.na}
            </p>
          </div> */}

          {/* API Error */}
          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="text-red-500 mr-3" size={20} />
              <div>
                <p className="font-medium text-red-800">
                  {t.global.error_title}
                </p>
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Full Address */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-2 text-blue-800 flex items-center">
                <MapPin size={18} className="mr-2" />
                {t.modules.kiosk.sections.A.questions.address}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                name="fullAddress"
                value={form.fullAddress}
                placeholder={t.global.location_placeholder}
                className={`w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${errors.fullAddress ? "border-red-500" : "border-blue-300"}`}
                rows="3"
                onChange={handleChange}
                required
              />
              {errors.fullAddress && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" />{" "}
                  {errors.fullAddress}
                </p>
              )}
              <p className="text-sm text-blue-600 mt-2">
                {t.global.location_hint}
              </p>
            </div>

            {/* Q1 - Functional */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A1. {t.modules.kiosk.sections.A.questions.functional}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex space-x-4 mb-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className="inline-flex items-center cursor-pointer"
                  >
                    <div className="relative">
                      <input
                        type="radio"
                        name="a1"
                        value={option}
                        checked={form.a1 === option}
                        onChange={handleChange}
                        className="sr-only"
                        required
                      />
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-2 ${form.a1 === option ? "border-blue-500 bg-blue-500" : "border-gray-300"}`}
                      >
                        {form.a1 === option && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              {errors.a1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.a1}
                </p>
              )}

              {form.a1 === t.global.no && (
                <div className="mt-3 space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t.global.remarks_label}
                    </label>
                    <textarea
                      name="a1Remarks"
                      placeholder={t.global.remarks_functional_placeholder}
                      className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                      rows="2"
                      onChange={handleChange}
                      value={form.a1Remarks}
                    />
                    {errors.a1Remarks && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />{" "}
                        {errors.a1Remarks}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-3">
                      {t.global.upload_label}
                    </label>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("a1Document");
                          setShowCamera(true);
                        }}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>
                      <label
                        htmlFor="a1Document"
                        className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 transition-all duration-150 cursor-pointer font-medium ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>
                    <input
                      type="file"
                      name="a1Document"
                      id="a1Document"
                      accept="image/*,.pdf"
                      onChange={handleChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition ${form.a1Document ? "border-green-400 bg-green-50" : "border-gray-300 bg-gray-50"}`}
                    >
                      {form.a1Document ? (
                        <div className="space-y-3">
                          {previewImages?.a1Document && (
                            <img
                              src={previewImages.a1Document}
                              alt="Preview"
                              onClick={() =>
                                setPreviewModal(previewImages.a1Document)
                              }
                              className="max-h-48 mx-auto rounded-lg shadow cursor-pointer hover:scale-105 transition"
                            />
                          )}
                          <div className="flex items-center justify-between">
                            <p className="text-gray-700 font-medium text-sm break-all">
                              {form.a1Document.name}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  a1Document: null,
                                }));
                                setPreviewImages((prev) => ({
                                  ...prev,
                                  a1Document: null,
                                }));
                              }}
                              className="text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          {t.global.capture_hint}
                        </p>
                      )}
                    </div>
                    {errors.a1Document && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.a1Document}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Q2 - Operated by */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A2. {t.modules.kiosk.sections.A.questions.operated}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex space-x-4 mb-3">
                {[t.global.shgs, t.global.others].map((option) => (
                  <label
                    key={option}
                    className="inline-flex items-center cursor-pointer"
                  >
                    <div className="relative">
                      <input
                        type="radio"
                        name="a2"
                        value={option}
                        checked={form.a2 === option}
                        onChange={handleChange}
                        className="sr-only"
                        required
                      />
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-2 ${form.a2 === option ? "border-blue-500 bg-blue-500" : "border-gray-300"}`}
                      >
                        {form.a2 === option && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              {errors.a2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.a2}
                </p>
              )}
              {form.a2 === t.global.others && (
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">
                    {t.global.remarks_label}
                  </label>
                  <textarea
                    name="a2Remarks"
                    placeholder={t.global.remarks_operated_placeholder}
                    className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                    rows="2"
                    onChange={handleChange}
                    value={form.a2Remarks}
                  />
                  {errors.a2Remarks && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />{" "}
                      {errors.a2Remarks}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Q3 - Staff count */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Users size={18} className="mr-2" />
                A3. {t.modules.kiosk.sections.A.questions.staff}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                name="a3"
                value={form.a3}
                placeholder={t.global.staff_placeholder}
                className={`w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.a3 ? "border-red-500" : "border-gray-300"}`}
                onChange={handleChange}
                min="0"
                required
              />
              {errors.a3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.a3}
                </p>
              )}
            </div>

            {/* Q4 - Timing */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Clock size={18} className="mr-2" />
                A4. {t.modules.kiosk.sections.A.questions.timing}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.global.timing_from}
                  </label>
                  <input
                    type="time"
                    name="a4From"
                    value={form.a4From}
                    onChange={handleChange}
                    className={`w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.a4 ? "border-red-500" : "border-gray-300"}`}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.global.timing_to}
                  </label>
                  <input
                    type="time"
                    name="a4To"
                    value={form.a4To}
                    onChange={handleChange}
                    className={`w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.a4 ? "border-red-500" : "border-gray-300"}`}
                    required
                  />
                </div>
              </div>
              {errors.a4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.a4}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-2">
                {t.global.timing_current}{" "}
                <span className="font-medium">
                  {form.a4From} {t.global.timing_to_word} {form.a4To}
                </span>
              </p>
            </div>

            {/* Q5 - Beneficiaries */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <UserCog size={18} className="mr-2" />
                A5. {t.modules.kiosk.sections.A.questions.beneficiaries}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                name="a5"
                value={form.a5}
                placeholder={t.global.beneficiaries_placeholder}
                className={`w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.a5 ? "border-red-500" : "border-gray-300"}`}
                onChange={handleChange}
                min="0"
                required
              />
              {errors.a5 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.a5}
                </p>
              )}
            </div>

            {/* Q6 - Food Coupons */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <FileText size={18} className="mr-2" />
                A6. {t.modules.kiosk.sections.A.questions.coupons}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex space-x-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className="inline-flex items-center cursor-pointer"
                  >
                    <div className="relative">
                      <input
                        type="radio"
                        name="a6"
                        value={option}
                        checked={form.a6 === option}
                        onChange={handleChange}
                        className="sr-only"
                        required
                      />
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-2 ${form.a6 === option ? "border-blue-500 bg-blue-500" : "border-gray-300"}`}
                      >
                        {form.a6 === option && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              {errors.a6 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.a6}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem(
                    "survey_section_a_kiosk",
                    JSON.stringify(form),
                  );
                  navigate("/survey/sections", {
                    state: { canteen, surveyId, newSurvey },
                  });
                }}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${loading || isSubmitting ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                {loading || isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    {t.global.submit_section_a}
                  </>
                )}
              </button>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-600 font-medium">
                  ({t.modules.kiosk.sections.A.mandatory})
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>

      {previewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-4 max-w-3xl w-full relative">
            <button
              type="button"
              onClick={() => setPreviewModal(null)}
              className="absolute top-3 right-3 text-gray-600 text-xl"
            >
              ✕
            </button>
            <img
              src={previewModal}
              alt="Preview"
              className="max-h-[75vh] mx-auto rounded-lg shadow-lg"
            />
          </div>
        </div>
      )}

      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-4 relative">
            <button
              onClick={() => setShowCamera(false)}
              className="absolute top-2 right-2 text-red-600 font-bold"
            >
              ✕
            </button>
            <CameraCapture
              onCapture={(file) => {
                setForm((prev) => ({ ...prev, [cameraField]: file }));
                setShowCamera(false);
              }}
              onClose={() => setShowCamera(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionAGeneralInformation;
